"use client"

import * as React from "react"
import { CheckIcon, MinusIcon, PlusIcon } from "lucide-react"

import { Button } from "@workspace/ui/components/button"
import { cn } from "@workspace/ui/lib/utils"

/**
 * One selectable option row of a modifier group (item customizer).
 *
 * `indicator` is VISUAL only (contract `SelectionRule.indicator`): `radio` /
 * `checkbox` show the native control, `none` hides it and lets the tile's
 * highlight carry the state. The native input stays in the DOM either way
 * (visually hidden for `none`), so assistive tech always gets a real radio or
 * checkbox with its checked state. A visible control takes the indicator's
 * shape (a single group may ask for checkboxes); a hidden one (`none`) uses
 * `inputType`.
 *
 * A multi-quantity option passes `stepper` instead of `onToggle`: it renders
 * add / remove-one buttons and a count, never a checkbox (re-tapping it ADDS a
 * unit, which a checkbox can't express).
 *
 * `indicator: "none"` (BMS `singleSimple` / `multiSimple`) renders a compact
 * CHIP instead of a full-width row — leading +/✓ circle, name, price (or the
 * note in its place) — meant
 * to sit in an {@link OptionTileList} grid. A multi-quantity chip adds a unit
 * per tap, shows the count in its circle and grows a remove-one button.
 *
 * Selection reads three ways — border, tinted ground and the control (or the
 * chip's filled circle) — so it survives a dark theme and a reader who can't
 * rely on hue alone.
 */
export interface OptionTileStepper {
  quantity: number
  onIncrement: () => void
  onDecrement: () => void
  canIncrement: boolean
  incrementLabel: string
  decrementLabel: string
}

export interface OptionTileProps {
  label: React.ReactNode
  /** Muted text right after the label ("included", "unavailable"). */
  note?: React.ReactNode
  description?: React.ReactNode
  /** Trailing price text. */
  price?: React.ReactNode
  /** Trailing badge text (e.g. "Free"); omitted ⇒ no badge. */
  badge?: React.ReactNode
  selected: boolean
  disabled?: boolean
  indicator: "radio" | "checkbox" | "none"
  inputType?: "radio" | "checkbox"
  /** Native `name` (radio groups). */
  inputName?: string
  onToggle?: () => void
  stepper?: OptionTileStepper
  className?: string
}

/** The list a group's tiles sit in: a chip grid for `none`, rows otherwise. */
export function OptionTileList({
  indicator,
  className,
  children,
}: {
  indicator: OptionTileProps["indicator"]
  className?: string
  children: React.ReactNode
}) {
  return (
    <ul
      data-slot="option-tile-list"
      className={cn(
        indicator === "none"
          ? // As many ~9rem chips per row as the CONTAINER fits (a modal, a
            // narrow column), not the viewport.
            "grid grid-cols-[repeat(auto-fill,minmax(9rem,1fr))] gap-2"
          : "flex flex-col gap-2",
        className
      )}
    >
      {children}
    </ul>
  )
}

export function OptionTile(props: OptionTileProps) {
  return props.indicator === "none" ? (
    <OptionChip {...props} />
  ) : (
    <OptionRow {...props} />
  )
}

/** Compact inline variant for `indicator: "none"` groups. */
function OptionChip({
  label,
  note,
  description,
  price,
  badge,
  selected,
  disabled = false,
  inputType = "checkbox",
  inputName,
  onToggle,
  stepper,
  className,
}: OptionTileProps) {
  const descriptionId = React.useId()
  const quantity = stepper?.quantity ?? 0
  // `relative`: contains the `sr-only` (absolutely positioned) input and
  // description. Without it they resolve against the nearest positioned
  // ancestor — a dialog's fixed content box — escape the scroll container
  // they sit in and stretch the dialog with a second scrollbar.
  const chipClass = cn(
    "relative flex h-full min-h-11 items-center gap-2 rounded-lg border bg-card px-2.5 py-2 text-sm shadow-xs transition-[background-color,border-color]",
    selected ? "border-primary/50 bg-primary/10" : "hover:bg-accent/60",
    disabled && "opacity-50 hover:bg-transparent",
    className
  )

  const circle = (
    <span
      aria-hidden
      className={cn(
        "flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
        selected
          ? "bg-primary text-primary-foreground"
          : "bg-muted text-foreground"
      )}
    >
      {stepper && quantity > 0 ? (
        quantity
      ) : selected ? (
        <CheckIcon className="size-3.5" />
      ) : (
        <PlusIcon className="size-3.5" />
      )}
    </span>
  )

  // Name and price share one wrapping row: on a chip too narrow for both, the
  // price drops under the name instead of the name breaking mid-word. A note
  // takes the price's place on that same row rather than a line of its own,
  // so a chip keeps its height when selecting something elsewhere notes it
  // ("included") — the whole grid row would otherwise jump with it.
  const body = (
    <>
      {circle}
      <span className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-x-2 gap-y-0.5 text-start leading-tight">
        <span className="max-w-full font-semibold break-words">{label}</span>
        {note || badge || price ? (
          <span className="flex min-w-0 items-baseline gap-1.5 text-xs font-semibold whitespace-nowrap">
            {badge ? <span className="text-primary">{badge}</span> : null}
            {note ? (
              <span className="truncate font-normal text-muted-foreground">
                {note}
              </span>
            ) : price ? (
              <span>{price}</span>
            ) : null}
          </span>
        ) : null}
      </span>
    </>
  )

  const describedBy = description ? descriptionId : undefined
  const srDescription = description ? (
    <span id={descriptionId} className="sr-only">
      {description}
    </span>
  ) : null

  if (stepper) {
    return (
      <div
        className={chipClass}
        title={typeof description === "string" ? description : undefined}
      >
        <button
          type="button"
          aria-label={stepper.incrementLabel}
          aria-describedby={describedBy}
          disabled={disabled || !stepper.canIncrement}
          onClick={stepper.onIncrement}
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-md outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed"
        >
          {body}
        </button>
        {quantity > 0 ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            // Taller than the circle: the negative margin keeps its hit area
            // without growing the chip when it appears.
            className="-my-1"
            aria-label={stepper.decrementLabel}
            onClick={stepper.onDecrement}
          >
            <MinusIcon aria-hidden />
          </Button>
        ) : null}
        {srDescription}
      </div>
    )
  }

  return (
    <label
      className={cn(
        chipClass,
        "has-[:focus-visible]:ring-[3px] has-[:focus-visible]:ring-ring/50",
        !disabled && "cursor-pointer"
      )}
      title={typeof description === "string" ? description : undefined}
    >
      <input
        type={inputType}
        name={inputType === "radio" ? inputName : undefined}
        checked={selected}
        disabled={disabled}
        // `click`, not `change` — see `OptionRow`.
        onClick={() => onToggle?.()}
        onChange={() => {}}
        aria-describedby={describedBy}
        className="sr-only"
      />
      {body}
      {srDescription}
    </label>
  )
}

/** Full-width row variant (radio / checkbox indicators). */
function OptionRow({
  label,
  note,
  description,
  price,
  badge,
  selected,
  disabled = false,
  indicator,
  inputType = "checkbox",
  inputName,
  onToggle,
  stepper,
  className,
}: OptionTileProps) {
  const descriptionId = React.useId()
  // `relative`: see `OptionChip` — keeps the `sr-only` input contained.
  const tileClass = cn(
    "relative flex min-h-11 items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm transition-[background-color,border-color]",
    selected ? "border-primary/40 bg-primary/10" : "hover:bg-accent/60",
    disabled && "opacity-50 hover:bg-transparent",
    className
  )

  // Wraps (note under the label) rather than running under the trailing
  // badge/price/stepper on a narrow tile.
  const text = (
    <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 break-words">
      <span className="font-medium">{label}</span>
      {note ? (
        <span className="text-xs text-muted-foreground">{note}</span>
      ) : null}
    </span>
  )

  const trailing = (
    <span className="flex shrink-0 items-center gap-2 text-xs whitespace-nowrap text-muted-foreground">
      {badge ? (
        <span className="rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary">
          {badge}
        </span>
      ) : null}
      {price ? <span>{price}</span> : null}
    </span>
  )

  return (
    <div className="flex flex-col gap-1">
      {stepper ? (
        <div className={tileClass}>
          <span className="flex min-w-0 flex-1 items-center gap-2">{text}</span>
          <span className="flex shrink-0 items-center gap-2">
            {trailing}
            <span className="flex items-center gap-1">
              {stepper.quantity > 0 ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon-sm"
                    aria-label={stepper.decrementLabel}
                    onClick={stepper.onDecrement}
                  >
                    <MinusIcon aria-hidden />
                  </Button>
                  <span
                    aria-live="polite"
                    className="w-5 text-center text-sm font-medium text-foreground"
                  >
                    {stepper.quantity}
                  </span>
                </>
              ) : null}
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                aria-label={stepper.incrementLabel}
                aria-describedby={description ? descriptionId : undefined}
                disabled={disabled || !stepper.canIncrement}
                onClick={stepper.onIncrement}
              >
                <PlusIcon aria-hidden />
              </Button>
            </span>
          </span>
        </div>
      ) : (
        <label className={cn(tileClass, !disabled && "cursor-pointer")}>
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <input
              // A visible control takes the indicator's shape (a single group
              // may ask for checkboxes); a hidden one keeps the semantics.
              type={indicator === "none" ? inputType : indicator}
              name={
                (indicator === "none" ? inputType : indicator) === "radio"
                  ? inputName
                  : undefined
              }
              checked={selected}
              disabled={disabled}
              // `click`, not `change`: a checked radio fires no change event,
              // and re-tapping the pick must still reach the rules (it clears
              // an optional single group). Arrow keys in a radio group and
              // Space on a checkbox dispatch click too.
              onClick={() => onToggle?.()}
              onChange={() => {}}
              aria-describedby={description ? descriptionId : undefined}
              className={cn(
                "size-4 shrink-0 accent-primary",
                indicator === "none" && "sr-only"
              )}
            />
            {text}
          </span>
          {trailing}
        </label>
      )}
      {description ? (
        <p id={descriptionId} className="ps-3 text-xs text-muted-foreground">
          {description}
        </p>
      ) : null}
    </div>
  )
}
